import { useState, useEffect, Fragment } from "react";
import { MapContainer, TileLayer, Circle, Polygon, Polyline, CircleMarker, Tooltip, useMapEvents, useMap } from "react-leaflet";
import api from "@/lib/api";
import { toast } from "sonner";
import { Circle as CircleIcon, Hexagon, X, Check } from "lucide-react";

const btn = (on) => `px-2.5 py-1 rounded-md text-xs border flex items-center gap-1 transition-colors ${on ? "border-[#17A2B8] text-[#2ec5db] bg-[#17A2B8]/10" : "border-border text-muted-foreground hover:text-foreground"}`;

function FlyToPoi({ poi }) {
  const map = useMap();
  useEffect(() => {
    if (!poi) return;
    let c = null;
    if (poi.lat != null && poi.lon != null) c = [poi.lat, poi.lon];
    else if (poi.coordinates && poi.coordinates.length) c = [poi.coordinates[0].lat, poi.coordinates[0].lon];
    if (c) map.panTo(c, { animate: true });
  }, [poi, map]);
  return null;
}

function mapDist(a, b) {
  const R = 6371000, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function Invalidate() {
  const map = useMap();
  useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 250); return () => clearTimeout(t); }, [map]);
  return null;
}

function DrawController({ mode, circle, setCircle, setPoly }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      if (mode === "circle") {
        if (!circle.center) setCircle({ center: { lat, lon: lng }, radius: 0 });
        else if (!circle.done) setCircle((c) => ({ ...c, radius: Math.round(mapDist(c.center, { lat, lon: lng })), done: true }));
      } else if (mode === "polygon") {
        setPoly((p) => [...p, { lat, lon: lng }]);
      }
    },
  });
  return null;
}

export default function ZoneEditor({ geofences = [], pois = [], highlightPoiId = null, onSaved }) {
  const [mode, setMode] = useState(null);
  const [circle, setCircle] = useState({});
  const [poly, setPoly] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "autorisee", color: "#F5821F" });
  const hasShape = (mode === "circle" && circle.done) || (mode === "polygon" && poly.length >= 3);

  const reset = () => { setMode(null); setCircle({}); setPoly([]); setForm({ name: "", kind: "autorisee", color: "#F5821F" }); };

  const save = async () => {
    if (!form.name) return toast.error("Nommez la zone");
    try {
      const body = mode === "circle"
        ? { name: form.name, type: "circle", kind: form.kind, color: form.color, radius: Math.round(circle.radius), center: circle.center }
        : { name: form.name, type: "polygon", kind: form.kind, color: form.color, coordinates: poly };
      await api.post("/geofences", body);
      toast.success("Zone créée");
      reset();
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="mb-4" data-testid="zone-editor">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="label-mono">Dessiner :</span>
        <button type="button" data-testid="draw-circle" onClick={() => { reset(); setMode("circle"); }} className={btn(mode === "circle")}><CircleIcon className="w-3.5 h-3.5" /> Cercle</button>
        <button type="button" data-testid="draw-polygon" onClick={() => { reset(); setMode("polygon"); }} className={btn(mode === "polygon")}><Hexagon className="w-3.5 h-3.5" /> Polygone</button>
        {mode && <button type="button" data-testid="draw-cancel" onClick={reset} className="px-2.5 py-1 rounded-md text-xs text-[#EF4444] flex items-center gap-1"><X className="w-3.5 h-3.5" /> Annuler</button>}
        {mode === "circle" && !circle.done && <span className="text-xs text-muted-foreground">Cliquez le centre, puis un point du bord.</span>}
        {mode === "polygon" && <span className="text-xs text-muted-foreground">Cliquez pour ajouter des sommets (min. 3).</span>}
      </div>
      <div className="rounded-lg overflow-hidden border border-border" style={{ height: 360 }}>
        <MapContainer center={[-22.2758, 166.458]} zoom={12} style={{ height: "100%", width: "100%" }}>
          <Invalidate />
          <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}" attribution="© Esri" />
          <DrawController mode={mode} circle={circle} setCircle={setCircle} setPoly={setPoly} />
          {geofences.map((g) => g.type === "circle" && g.center ? (
            <Circle key={g.id} center={[g.center.lat, g.center.lon]} radius={g.radius} pathOptions={{ color: g.color, fillColor: g.color, fillOpacity: 0.08, weight: 1.5, dashArray: g.kind === "interdite" ? "6 6" : null }} />
          ) : g.type === "polygon" && g.coordinates ? (
            <Polygon key={g.id} positions={g.coordinates.map((p) => [p.lat, p.lon])} pathOptions={{ color: g.color, fillColor: g.color, fillOpacity: 0.08, weight: 1.5, dashArray: g.kind === "interdite" ? "6 6" : null }} />
          ) : null)}
          {pois.map((p) => {
            const col = p.color || "#17A2B8";
            const hot = p.id === highlightPoiId;
            const opts = { color: col, fillColor: col, fillOpacity: hot ? 0.35 : 0.12, weight: hot ? 4 : 2 };
            if (p.type === "polygon" && p.coordinates)
              return <Polygon key={p.id} positions={p.coordinates.map((c) => [c.lat, c.lon])} pathOptions={opts}><Tooltip permanent={hot}>{p.name}</Tooltip></Polygon>;
            if (p.type === "troncon" && p.coordinates)
              return <Polyline key={p.id} positions={p.coordinates.map((c) => [c.lat, c.lon])} pathOptions={{ ...opts, weight: hot ? 6 : 4 }}><Tooltip permanent={hot}>{p.name}</Tooltip></Polyline>;
            if (p.lat != null && p.lon != null)
              return (
                <Fragment key={p.id}>
                  {p.radius ? <Circle center={[p.lat, p.lon]} radius={p.radius} pathOptions={{ ...opts, fillOpacity: hot ? 0.25 : 0.08 }} /> : null}
                  <CircleMarker center={[p.lat, p.lon]} radius={hot ? 10 : 6} pathOptions={{ color: col, fillColor: col, fillOpacity: 0.9, weight: hot ? 4 : 2 }}><Tooltip permanent={hot}>{p.name}</Tooltip></CircleMarker>
                </Fragment>
              );
            return null;
          })}
          <FlyToPoi poi={pois.find((p) => p.id === highlightPoiId)} />
          {mode === "circle" && circle.center && (
            <>
              <CircleMarker center={[circle.center.lat, circle.center.lon]} radius={4} pathOptions={{ color: form.color }} />
              {circle.radius > 0 && <Circle center={[circle.center.lat, circle.center.lon]} radius={circle.radius} pathOptions={{ color: form.color, fillColor: form.color, fillOpacity: 0.12, weight: 2 }} />}
            </>
          )}
          {mode === "polygon" && poly.length > 0 && (
            <>
              <Polyline positions={poly.map((p) => [p.lat, p.lon])} pathOptions={{ color: form.color, weight: 2, dashArray: "4 4" }} />
              {poly.map((p, i) => <CircleMarker key={i} center={[p.lat, p.lon]} radius={4} pathOptions={{ color: form.color }} />)}
            </>
          )}
        </MapContainer>
      </div>
      {hasShape && (
        <div className="glass rounded-lg p-3 mt-2 flex flex-wrap items-end gap-3" data-testid="zone-save-form">
          <div><label className="label-mono block mb-1">Nom</label><input data-testid="zone-name" className="bg-secondary/40 border border-border rounded-md px-3 py-1.5 text-sm" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
          <div><label className="label-mono block mb-1">Type</label>
            <select data-testid="zone-kind" className="bg-secondary/40 border border-border rounded-md px-3 py-1.5 text-sm" value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}>
              <option value="autorisee">Autorisée</option><option value="interdite">Interdite</option>
            </select>
          </div>
          <div><label className="label-mono block mb-1">Couleur</label><input type="color" className="h-[34px] w-12 bg-transparent border border-border rounded-md p-1" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} /></div>
          <button type="button" onClick={save} data-testid="zone-save" className="bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Enregistrer la zone</button>
          <button type="button" onClick={reset} className="text-xs text-muted-foreground">Recommencer</button>
        </div>
      )}
    </div>
  );
}
